import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import axios from 'axios';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://api.mbsmartweb.com/api";

const Statement3 = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortOrder, setSortOrder] = useState('desc');
  const currentUserId = localStorage.getItem("UserId");

  useEffect(() => {
    if (currentUserId) {
      fetchTransactions();
    }
  }, [currentUserId]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API_BASE}/statement3`, {
        UserId: currentUserId
      });
      
      if (response.data.error) {
        setTransactions([]);
        if (response.data.error !== "No transactions found") {
          toast.info(response.data.error);
        }
      } else {
        setTransactions(response.data);
      }
    } catch (err) {
      console.error("Error fetching transactions:", err);
      toast.error("Failed to load transaction history");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today ' + date.toLocaleTimeString();
    if (diffDays === 1) return 'Yesterday ' + date.toLocaleTimeString();
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getFilteredTransactions = () => {
    let filtered = [...transactions];
    
    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(t => t.Type === filterType);
    }
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.Desc.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.Mode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.Tid.toString().includes(searchTerm)
      );
    }
    
    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.Time);
      const dateB = new Date(b.Time);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
    
    return filtered;
  };

  const filteredTransactions = getFilteredTransactions();

const getStatistics = () => {
  const totalCredit = filteredTransactions
    .filter(t => t.Type?.toUpperCase() === 'CREDIT')
    .reduce((sum, t) => sum + t.amount, 0);
  
  const totalDebit = filteredTransactions
    .filter(t => t.Type?.toUpperCase() === 'DEBIT')
    .reduce((sum, t) => sum + t.amount, 0);
  
  return {
    totalCredit,
    totalDebit,
    balance: totalCredit - totalDebit
  };
};

  const stats = getStatistics();

  return (
    <Layout>
      <ToastContainer position="top-center" autoClose={3000} />
      
      <div className="statement-page">
        <div className="page-header">
          <h1>
            <i className="fa fa-history"></i>
            Complete Transaction History
          </h1>
          <p>Full statement of all your transactions</p>
        </div>

        {/* Quick Stats */}
        <div className="quick-stats">
          <div className="stat-box">
            <i className="fa fa-arrow-up text-success"></i>
            <div>
              <small>Total Credit</small>
              <strong>{formatAmount(stats.totalCredit)}</strong>
            </div>
          </div>
          <div className="stat-box">
            <i className="fa fa-arrow-down text-danger"></i>
            <div>
              <small>Total Debit</small>
              <strong>{formatAmount(stats.totalDebit)}</strong>
            </div>
          </div>
          <div className="stat-box">
            <i className="fa fa-balance-scale"></i>
            <div>
              <small>Net Balance</small>
              <strong>{formatAmount(stats.balance)}</strong>
            </div>
          </div>
          <div className="stat-box">
            <i className="fa fa-list-ul"></i>
            <div>
              <small>Transactions</small>
              <strong>{filteredTransactions.length}</strong>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="search-filters">
          <div className="search-box">
            <i className="fa fa-search"></i>
            <input
              type="text"
              placeholder="Search by description, mode, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            <option value="Credit">Credit Only</option>
            <option value="Debit">Debit Only</option>
          </select>
          
          <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
          
          <button className="btn-refresh" onClick={fetchTransactions}>
            <i className="fa fa-refresh"></i>
          </button>
        </div>

        {/* Transactions Table View */}
        <div className="transactions-container">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading transactions...</p>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="empty-state">
              <i className="fa fa-receipt"></i>
              <p>No transactions found</p>
              <button className="btn-primary" onClick={fetchTransactions}>
                Refresh
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="transactions-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Description</th>
                    <th>Mode</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((transaction) => (
                    <tr key={transaction.Tid}>
                      <td className="tid">#{transaction.Tid}</td>
                      <td className="desc">{transaction.Desc}</td>
                      <td>
                        <span className="mode-badge">{transaction.Mode}</span>
                      </td>
                      <td>
                        <span className={`type-badge ${transaction.Type.toLowerCase()}`}>
                          {transaction.Type}
                        </span>
                      </td>
                      <td className={`amount ${transaction.Type.toLowerCase()}`}>
                        {transaction.Type === 'Credit' ? '+' : '-'} {formatAmount(transaction.amount)}
                      </td>
                      <td className="date">{formatDate(transaction.Time)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .statement-page {
          max-width: 1400px;
          margin: 0 auto;
          padding: 20px;
          animation: fadeIn 0.5s ease;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .page-header {
          text-align: center;
          margin-bottom: 30px;
        }
        
        .page-header h1 {
          font-size: 32px;
          font-weight: 800;
          background: linear-gradient(135deg, #667eea, #764ba2);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 10px;
        }
        
        .page-header p {
          color: #718096;
        }
        
        /* Quick Stats */
        .quick-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
          margin-bottom: 25px;
        }
        
        .stat-box {
          background: white;
          padding: 15px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 15px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
          transition: transform 0.2s;
        }
        
        .stat-box:hover {
          transform: translateY(-3px);
          box-shadow: 0 5px 15px rgba(0,0,0,0.1);
        }
        
        .stat-box i {
          font-size: 32px;
        }
        
        .stat-box small {
          display: block;
          font-size: 11px;
          color: #718096;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .stat-box strong {
          font-size: 20px;
          color: #2d3748;
        }
        
        .text-success { color: #48bb78; }
        .text-danger { color: #f56565; }
        
        /* Search Filters */
        .search-filters {
          background: white;
          border-radius: 12px;
          padding: 15px;
          margin-bottom: 25px;
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .search-box {
          flex: 1;
          position: relative;
        }
        
        .search-box i {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #a0aec0;
        }
        
        .search-box input {
          width: 100%;
          padding: 10px 10px 10px 35px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          transition: all 0.2s;
        }
        
        .search-box input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }
        
        .search-filters select {
          padding: 10px 15px;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .search-filters select:focus {
          outline: none;
          border-color: #667eea;
        }
        
        .btn-refresh {
          padding: 10px 20px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .btn-refresh:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
        }
        
        /* Transactions Container */
        .transactions-container {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }
        
        .table-responsive {
          overflow-x: auto;
        }
        
        .transactions-table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .transactions-table th {
          text-align: left;
          padding: 15px;
          background: #f7fafc;
          font-weight: 600;
          color: #4a5568;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .transactions-table td {
          padding: 15px;
          border-bottom: 1px solid #edf2f7;
        }
        
        .transactions-table tbody tr:hover {
          background: #f7fafc;
          transition: background 0.2s;
        }
        
        .tid {
          font-weight: 600;
          color: #667eea;
        }
        
        .desc {
          max-width: 300px;
          word-wrap: break-word;
        }
        
        .mode-badge {
          display: inline-block;
          padding: 4px 8px;
          background: #687ae4;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 500;
        }
        
        .type-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }
        
        .type-badge.credit {
          background: #c6f6d5;
          color: #22543d;
        }
        
        .type-badge.debit {
          background: #fed7d7;
          color: #742a2a;
        }
        
        .amount {
          font-weight: 800;
          font-size: 16px;
        }
        
        .amount.credit {
          color: #48bb78;
        }
        
        .amount.debit {
          color: #f56565;
        }
        
        .date {
          font-size: 13px;
          color: #718096;
        }
        
        /* Loading State */
        .loading-state {
          text-align: center;
          padding: 60px 20px;
        }
        
        .spinner {
          width: 50px;
          height: 50px;
          border: 3px solid #edf2f7;
          border-top-color: #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }
        
        .empty-state i {
          font-size: 64px;
          color: #cbd5e0;
          margin-bottom: 20px;
        }
        
        .empty-state p {
          color: #718096;
          margin-bottom: 20px;
        }
        
        .btn-primary {
          padding: 10px 24px;
          background: linear-gradient(135deg, #667eea, #764ba2);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s;
        }
        
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .quick-stats {
            grid-template-columns: repeat(2, 1fr);
            gap: 10px;
          }
          
          .stat-box strong {
            font-size: 16px;
          }
          
          .stat-box i {
            font-size: 24px;
          }
          
          .search-filters {
            flex-direction: column;
          }
          
          .search-box {
            width: 100%;
          }
          
          .search-filters select {
            width: 100%;
          }
          
          .btn-refresh {
            width: 100%;
            justify-content: center;
          }
          
          .transactions-table th,
          .transactions-table td {
            padding: 10px;
            font-size: 12px;
          }
          
          .amount {
            font-size: 14px;
          }
        }
        
        @media (max-width: 480px) {
          .quick-stats {
            grid-template-columns: 1fr;
          }
          
          .stat-box {
            padding: 12px;
          }
        }
      `}</style>
    </Layout>
  );
};

export default Statement3;